import type { CollectionReference, DocumentSnapshot } from 'firebase/firestore'
import { collection, getDocs, query, where } from '@/utils/firebase/lazyFirestore'
import { useFirebase } from '@/composables/firebase/useFirebase'
import { slugify } from '#shared/utils/slug'

/**
 * Composable for slug-related functionality
 */
export function useSlug() {
    const { $firebase } = useFirebase()

    // Implement resolveCollection function directly
    function resolveCollection(collectionRef: string | CollectionReference): CollectionReference {
        if (typeof collectionRef === 'string') {
            return collection($firebase.db, collectionRef)
        }
        return collectionRef
    }

    /**
     * Check if a slug is available (not already used in a collection)
     */
    async function validateSlug(
        collectionRef: string | CollectionReference,
        slug: string,
        excludeId?: string,
        attribute?: string
    ): Promise<boolean> {
        const snapshot = await getDocs(query(resolveCollection(collectionRef), where(attribute || 'slug', '==', slug)))

        return snapshot.docs.filter((doc: DocumentSnapshot) => doc.id !== excludeId).length === 0
    }

    /**
     * Generate a unique slug for a given title
     * If the slug already exists, it adds a numeric suffix
     */
    function resolveSlug(
        collectionRef: string | CollectionReference,
        title: string,
        excludeId?: string,
        attribute?: string
    ): Promise<string> {
        const slug = slugify(title)
        const MAX_ITERATIONS = 50

        const routine = async (iteration: number = 1): Promise<string> => {
            const slugWithSuffix = `${slug}${iteration > 1 ? `-${iteration}` : ''}`

            const isAvailable = await validateSlug(collectionRef, slugWithSuffix, excludeId, attribute)
            if (isAvailable) {
                return slugWithSuffix
            }

            if (iteration >= MAX_ITERATIONS) {
                // Fallback: append random suffix to guarantee uniqueness
                return `${slug}-${Date.now().toString(36)}`
            }

            return routine(iteration + 1)
        }

        return routine()
    }

    return {
        slugify,
        validateSlug,
        resolveSlug
    }
}
